import {
	type GetImpulseFormInput,
	type GetImpulseFormInputSetter,
	ImpulseFormList,
	ImpulseFormShape,
} from "~/tools/impulse-form";
import { setFormElementsInput } from "~/tools/impulse-form/set-form-elements-input";
import { isDeepEqual } from "~/tools/is-deep-equal";
import { isPresent } from "~/tools/is-present";
import type { Partial } from "~/tools/partial";

import { WidgetCalculation } from "./calculation/form-state";
import { WidgetColor } from "./color/form-state";
import { WidgetDecimals } from "./decimals/form-state";
import { WidgetRange } from "./range/form-state";
import { type WidgetThresholdsColor, WidgetThresholdsMode, WidgetThresholdsStep } from "./thresholds/form-state";
import { WidgetUnit } from "./unit/form-state";
import type { WidgetFormSync } from "./sync";

type GaugeChartFormState = ImpulseFormShape<{
	unit: WidgetUnit;
	decimals: WidgetDecimals;
	max: WidgetRange;
	calculation: WidgetCalculation;
	color: WidgetThresholdsColor;

	thresholds: ImpulseFormShape<{
		mode: WidgetThresholdsMode;
		steps: ImpulseFormList<WidgetThresholdsStep>;
	}>;
}>;

type Input = GetImpulseFormInput<GaugeChartFormState>;

const DEFAULT = {
	unit: WidgetUnit.default,
	decimals: WidgetDecimals.default,
	max: WidgetRange.default,
	calculation: WidgetCalculation.default,
	color: WidgetColor.healthy,
	thresholds: {
		mode: WidgetThresholdsMode.default,
		steps: [
			{ value: "0.9", color: WidgetColor.preset[0] },
			{ value: "0.8", color: WidgetColor.preset[1] },
		],
	},
} satisfies Input;

function init(initial?: Partial<Input, "deep">): GaugeChartFormState {
	const steps =
		initial?.thresholds?.steps == null || initial.thresholds.steps.length === 0
			? DEFAULT.thresholds.steps
			: initial.thresholds.steps;

	return ImpulseFormShape({
		unit: WidgetUnit.init(initial?.unit),
		decimals: WidgetDecimals.init(initial?.decimals),
		max: WidgetRange.init(initial?.max),
		calculation: WidgetCalculation.init(initial?.calculation),
		color: WidgetColor.init(initial?.color ?? DEFAULT.color),
		thresholds: ImpulseFormShape({
			mode: WidgetThresholdsMode.init(initial?.thresholds?.mode),
			steps: ImpulseFormList(steps.map(WidgetThresholdsStep.init)),
		}),
	});
}

function setInput(
	{ unit, decimals, max, calculation, color, thresholds }: Partial<Input, "deep">,
	state: GaugeChartFormState,
): void {
	state.setInput({
		unit,
		decimals,
		max,
		calculation,
		color,
		thresholds: {
			mode: thresholds?.mode,
		},
	});

	if (thresholds?.steps != null) {
		const steps = thresholds.steps.filter(isPresent);

		setFormElementsInput(
			(threshold) => WidgetThresholdsStep.init(threshold),
			steps.length === 0 ? DEFAULT.thresholds.steps : steps,
			state.fields.thresholds.fields.steps,
		);
	}
}

function fromSync({ unit, decimals, max, calculation, color, thresholds }: WidgetFormSync) {
	return {
		unit,
		decimals,
		max,
		calculation,
		color,
		thresholds: {
			mode: thresholds?.mode,
			steps: thresholds?.steps,
		},
	} satisfies GetImpulseFormInputSetter<GaugeChartFormState>;
}

function toSync({ unit, decimals, max, calculation, color, thresholds }: Input) {
	return {
		unit,
		decimals,
		max,
		calculation,
		color,
		thresholds: {
			mode: thresholds?.mode,
			steps:
				thresholds?.steps != null && isDeepEqual(thresholds.steps, DEFAULT.thresholds.steps) ? [] : thresholds?.steps,
		},
	} satisfies WidgetFormSync;
}

const GaugeChartFormState = {
	default: DEFAULT,
	init,
	setInput,
	toSync,
	fromSync,
} as const;

export { GaugeChartFormState };
